'use client';
import React, { forwardRef, JSX, useCallback, useEffect, useRef } from 'react';
import { cn } from '@fshr/shared-react';
import Cleave from 'cleave.js';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	wrapperClassName?: string;
	label?: string;
	labelStyle?: string;
	labelTextClassName?: string;
	errorMessage?: string;
	blacklistedCharacters?: string;
	icon?: JSX.Element;
	onClickIcon?: () => void;
	autoComplete?: string;
	errorRef?: React.RefObject<HTMLInputElement | null>;
}

type CleaveInstance = {
	destroy: () => void;
	setRawValue: (value: string) => void;
};

const stripCardValue = (value: InputProps['value'], blacklistedCharacters?: string) => {
	const text = value == null ? '' : String(value);
	if (!blacklistedCharacters) {
		return text;
	}
	return text.replace(new RegExp(`[${blacklistedCharacters}]`, 'g'), '');
};

const CreditCardNumber = forwardRef(
	(
		{
			wrapperClassName,
			className,
			readOnly,
			label,
			labelStyle,
			id,
			name,
			errorMessage,
			required,
			blacklistedCharacters,
			icon,
			onClickIcon,
			labelTextClassName,
			autoComplete = 'cc-number',
			errorRef,
			value,
			onChange,
			placeholder,
			...props
		}: InputProps,
		ref: React.Ref<HTMLInputElement>
	) => {
		const inputRef = useRef<HTMLInputElement | null>(null);
		const cleaveRef = useRef<CleaveInstance | null>(null);
		const onChangeRef = useRef(onChange);
		const forwardedRef = useRef(ref);
		const errorRefHolder = useRef(errorRef);
		const emittedValue = useRef<string | null>(null);
		onChangeRef.current = onChange;
		forwardedRef.current = ref;
		errorRefHolder.current = errorRef;

		const assignInput = useCallback((node: HTMLInputElement | null) => {
			inputRef.current = node;
			if (errorRefHolder.current) {
				errorRefHolder.current.current = node;
			}
			const outer = forwardedRef.current;
			if (typeof outer === 'function') {
				outer(node as HTMLInputElement);
			} else if (outer) {
				outer.current = node;
			}
		}, []);

		useEffect(() => {
			const el = inputRef.current;
			if (!el) {
				return;
			}

			const cleave = new Cleave(el, {
				creditCard: true,
				onValueChanged: (event: { target: { value: string } }) => {
					const next = event.target.value ?? '';
					if (next === emittedValue.current) {
						return;
					}
					emittedValue.current = next;
					onChangeRef.current?.({
						target: { value: next, name: el.name },
						currentTarget: { value: next, name: el.name },
					} as React.ChangeEvent<HTMLInputElement>);
				},
			}) as CleaveInstance;
			cleaveRef.current = cleave;

			return () => {
				cleave.destroy();
				cleaveRef.current = null;
			};
		}, []);

		useEffect(() => {
			const cleave = cleaveRef.current;
			const el = inputRef.current;
			if (!cleave || !el) {
				return;
			}
			const next = stripCardValue(value, blacklistedCharacters);
			if (emittedValue.current === next || el.value === next) {
				emittedValue.current = next;
				return;
			}
			cleave.setRawValue(next.replace(/ /g, ''));
		}, [value, blacklistedCharacters]);

		return (
			<div
				className={cn('relative w-full', wrapperClassName, {
					'pt-200': label,
					'text-red-700': errorMessage,
					'pb-[20px] lg:pb-[28px]': !readOnly,
				})}
			>
				<div className="relative">
					<input
						{...props}
						ref={assignInput}
						className={cn(
							[
								'peer',
								'h-[47px]',
								'mt-[2px]',
								'border-black',
								'text-body2',
								'focus:outline-hidden',
								'w-full',
								'placeholder:transition-opacity',
								'placeholder:opacity-100!',
								'placeholder:duration-300!',
								'placeholder:text-black!',
								'focus:placeholder:opacity-0!',
							],
							{
								'border-b bg-surface-form px-4': !readOnly,
								'placeholder:text-red-700! border-red-700': errorMessage,
							},
							className
						)}
						placeholder={placeholder && required ? `${placeholder}*` : placeholder}
						required={required}
						defaultValue=""
						maxLength={30}
						minLength={8}
						id={id}
						name={name}
						readOnly={readOnly}
						aria-labelledby={label && `${id}-label`}
						autoComplete={autoComplete}
					/>
					{label && (
						<label
							className={cn(
								'text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-[18px] peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-0 peer-focus:opacity-100 peer-focus:-translate-y-[18px]',
								labelStyle
							)}
							htmlFor={id}
							id={id && `${id}-label`}
						>
							<span className={cn(labelTextClassName)}>{`${label}${required ? '*' : ''}`}</span>
						</label>
					)}

					{icon ? (
						<div
							className="absolute top-[15.5px] right-[16px] cursor-pointer"
							onClick={onClickIcon}
						>
							{icon}
						</div>
					) : null}
				</div>
				{errorMessage && (
					<span className="absolute lg:pt-[2px] text-caption text-red-700" role="alert">
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default CreditCardNumber;
